import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useRef, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import useClickOutside from '../../hooks/useClickOutside';

import { useCurrentPage } from '../../contexts/PagesContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import { useLocales } from '../../contexts/SiteContext';
import PillButton from '../buttons/Pill';
import Menu from '../menus/Menu';

import styles from '../../../styles/menus/locales-menu.module.scss';

const propTypes = {
    className: PropTypes.string,
    buttonClassName: PropTypes.string,
    onClick: PropTypes.func,
    isDark: PropTypes.bool,
    isContrast: PropTypes.bool,
};

const defaultProps = {
    className: null,
    buttonClassName: null,
    onClick: null,
    isDark: false,
    isContrast: false,
};

const LocalesMenu = ({ className, buttonClassName, onClick, isDark, isContrast }) => {
    const ref = useRef(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const route = useUrlGenerator();
    const { locale } = useIntl();
    const locales = useLocales();
    const page = useCurrentPage();
    const { metadata: { urls = {} } = {} } = page || {};

    const items = useMemo(
        () =>
            locales.map((it) => ({
                id: it,
                label: it,
                url: urls[it] || route(`${it}.home`),
                external: true,
                target: '_self',
                rel: null,
                active: it === locale,
            })),
        [route, locales, locale, urls],
    );

    const active = items.find((i) => i.active);
    const others = items.filter((i) => !i.active);

    const onClickBtn = useCallback(
        (e) => {
            if (onClick !== null) onClick(e);
            setDropdownOpen(!dropdownOpen);
        },
        [dropdownOpen, setDropdownOpen],
    );

    const onClickOutside = useCallback(() => {
        setDropdownOpen(false);
    }, [setDropdownOpen]);

    useClickOutside(ref, onClickOutside);

    // only one other language? just use a single button
    if (others.length === 1) {
        return (
            <PillButton
                onClick={onClickBtn}
                href={others[0].url}
                className={classNames([
                    styles.dropdownBtn,
                    {
                        [buttonClassName]: buttonClassName !== null,
                    },
                ])}
                isDark={isDark}
                isContrast={isContrast}
                target="_self"
                external
            >
                {others[0].label}
            </PillButton>
        );
    }

    return (
        <div
            ref={ref}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                    [styles.isDropdownOpen]: dropdownOpen,
                },
            ])}
        >
            <PillButton
                onClick={onClickBtn}
                className={classNames([
                    styles.dropdownBtn,
                    {
                        [buttonClassName]: buttonClassName !== null,
                    },
                ])}
                isDark={isDark}
            >
                {active.label}
            </PillButton>

            <Menu
                items={others}
                className={styles.dropdown}
                itemsClassName={styles.items}
                itemClassName={styles.item}
                activeItemClassName={styles.active}
                linkClassName={styles.link}
            />
        </div>
    );
};

LocalesMenu.propTypes = propTypes;
LocalesMenu.defaultProps = defaultProps;

export default LocalesMenu;
